/**
 * Media Load Gate for 1721 Comma Sofa
 * Part 2 of 2 (works with media-load-gate-early.liquid)
 *
 * The early interceptor (in <head>) has already stripped src from
 * all product images except the first hero. This script now:
 *
 * 1. Waits for Star App to finish showing/hiding variant images
 * 2. Restores src ONLY on images Star App has made visible
 * 3. Watches for variant changes and repeats the process
 *
 * Zero configuration. Zero alt text. Zero manual work.
 *
 * INSTALL:
 *   Upload to Theme > Assets
 *   Add script tag to product template (see guide)
 */

(function () {
  'use strict';

  /* ── SELECTORS ────────────────────────────────────────────────── */
  var MEDIA_SELECTORS = [
    '.product__media-item',
    '.product__media',
    '.product-gallery__slide',
    '[data-product-media-item]',
    '.product__media-list > li',
    '.product__media-list > div',
    '.product-gallery .swiper-slide',
    '.product__media-wrapper .swiper-slide'
  ].join(', ');

  var THUMB_SELECTORS = [
    '.product__thumbnail-item',
    '.product-gallery__thumb',
    '[data-product-thumbnail-item]',
    '.product__thumbnail-list > li',
    '.product__thumbnail-list > div'
  ].join(', ');

  var scanTimer = null;
  var isRunning = false;

  /* ── VISIBILITY CHECK ─────────────────────────────────────────── */
  function isHiddenByApp(el) {
    var current = el;
    var depth = 0;

    while (current && depth < 10) {
      try {
        var style = window.getComputedStyle(current);
        if (style.display === 'none' ||
            style.visibility === 'hidden' ||
            style.opacity === '0') {
          return true;
        }
      } catch (e) {}

      if (current.classList) {
        if (current.classList.contains('hidden') ||
            current.classList.contains('hide') ||
            current.classList.contains('is-hidden') ||
            current.classList.contains('d-none')) {
          return true;
        }
      }

      if (current.getAttribute('aria-hidden') === 'true') {
        return true;
      }

      if (current.offsetHeight === 0 && current.offsetWidth === 0 &&
          current.tagName !== 'IMG' && current.tagName !== 'SOURCE') {
        return true;
      }

      /* Stop at gallery wrapper */
      if (current.classList &&
          (current.classList.contains('product__media-list') ||
           current.classList.contains('product-gallery') ||
           current.classList.contains('product__media-wrapper'))) {
        break;
      }

      current = current.parentElement;
      depth++;
    }

    return false;
  }

  /* ── WEBP CONVERSION ──────────────────────────────────────────── */
  /**
   * Rewrites a Shopify CDN image URL to request WebP format.
   * Shopify CDN supports ?format=webp on all cdn.shopify.com image URLs.
   * Only modifies Shopify CDN URLs. Leaves external URLs untouched.
   * Also applies to every URL inside a srcset string.
   */
  function toWebP(url) {
    if (!url || url.indexOf('data:') === 0) return url;

    /* Only rewrite Shopify CDN URLs */
    if (url.indexOf('cdn.shopify.com') === -1) return url;

    /* Already has format param: replace it with webp */
    if (url.indexOf('format=') !== -1) {
      return url.replace(/format=[^&]+/, 'format=webp');
    }

    /* Append format=webp */
    var separator = url.indexOf('?') !== -1 ? '&' : '?';
    return url + separator + 'format=webp';
  }

  function toWebPSrcset(srcset) {
    if (!srcset) return srcset;

    /* srcset format: "url1 800w, url2 1200w, ..." */
    return srcset.split(',').map(function(part) {
      var trimmed = part.trim();
      var spaceIndex = trimmed.lastIndexOf(' ');

      if (spaceIndex !== -1) {
        var url = trimmed.substring(0, spaceIndex);
        var descriptor = trimmed.substring(spaceIndex);
        return toWebP(url) + descriptor;
      }

      return toWebP(trimmed);
    }).join(', ');
  }

  /* ── RESTORE / DISABLE IMAGES ─────────────────────────────────── */
  function activateImage(img, isHero) {
    var realSrc = img.getAttribute('data-mlg-src');
    var realSrcset = img.getAttribute('data-mlg-srcset');

    if (realSrc) {
      img.src = toWebP(realSrc);
    }
    if (realSrcset) {
      img.srcset = toWebPSrcset(realSrcset);
    }

    if (isHero) {
      img.loading = 'eager';
      img.fetchPriority = 'high';
    } else {
      img.loading = 'lazy';
    }

    img.setAttribute('data-mlg-active', 'true');
  }

  function deactivateImage(img) {
    /* Store current src if not already stored */
    if (img.src && img.src.indexOf('data:') !== 0 && !img.getAttribute('data-mlg-src')) {
      img.setAttribute('data-mlg-src', img.src);
    }
    if (img.srcset && !img.getAttribute('data-mlg-srcset')) {
      img.setAttribute('data-mlg-srcset', img.srcset);
    }

    img.removeAttribute('srcset');
    img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    img.loading = 'lazy';
    img.removeAttribute('data-mlg-active');
  }

  function activateVideo(video) {
    var src = video.getAttribute('data-mlg-video-src');
    if (src) video.src = src;

    var sources = video.querySelectorAll('source');
    sources.forEach(function(s) {
      var vSrc = s.getAttribute('data-mlg-video-src');
      if (vSrc) s.src = vSrc;
    });

    video.preload = 'metadata';
  }

  function deactivateVideo(video) {
    if (video.src && !video.getAttribute('data-mlg-video-src')) {
      video.setAttribute('data-mlg-video-src', video.src);
    }
    video.removeAttribute('src');

    var sources = video.querySelectorAll('source');
    sources.forEach(function(s) {
      if (s.src && !s.getAttribute('data-mlg-video-src')) {
        s.setAttribute('data-mlg-video-src', s.src);
      }
      s.removeAttribute('src');
    });

    video.preload = 'none';
    video.pause();
  }

  /* ── MAIN SCAN ────────────────────────────────────────────────── */
  function scanAndGate() {
    var heroSet = false;

    /* Gallery images */
    var mediaItems = document.querySelectorAll(MEDIA_SELECTORS);

    mediaItems.forEach(function(item) {
      var hidden = isHiddenByApp(item);

      item.querySelectorAll('img').forEach(function(img) {
        if (hidden) {
          deactivateImage(img);
        } else {
          var isHero = !heroSet;
          activateImage(img, isHero);
          if (isHero) heroSet = true;
        }
      });

      item.querySelectorAll('video').forEach(function(video) {
        if (hidden) {
          deactivateVideo(video);
        } else {
          activateVideo(video);
        }
      });
    });

    /* Thumbnails */
    var thumbItems = document.querySelectorAll(THUMB_SELECTORS);

    thumbItems.forEach(function(thumb) {
      var hidden = isHiddenByApp(thumb);

      thumb.querySelectorAll('img').forEach(function(img) {
        if (hidden) {
          deactivateImage(img);
        } else {
          activateImage(img, false);
        }
      });
    });

    /* Reinitialise sliders */
    reinitSlider();
  }

  function debouncedScan() {
    if (scanTimer) clearTimeout(scanTimer);
    scanTimer = setTimeout(scanAndGate, 80);
  }

  /* ── SLIDER REINIT ────────────────────────────────────────────── */
  function reinitSlider() {
    try {
      /* Flickity */
      var flickEl = document.querySelector('.product__media-list.flickity-enabled');
      if (flickEl && window.Flickity) {
        var flkty = window.Flickity.data(flickEl);
        if (flkty) { flkty.reloadCells(); flkty.resize(); flkty.select(0, false, true); }
      }

      /* Swiper */
      var swiperEl = document.querySelector('.product__media-list .swiper, .product-gallery .swiper');
      if (swiperEl && swiperEl.swiper) { swiperEl.swiper.update(); swiperEl.swiper.slideTo(0, 0); }

      /* Splide */
      var splideEl = document.querySelector('.product__media-list.splide');
      if (splideEl && splideEl.splide) { splideEl.splide.refresh(); splideEl.splide.go(0); }

      window.dispatchEvent(new Event('resize'));
    } catch (e) {}
  }

  /* ── VARIANT CHANGE WATCHERS ──────────────────────────────────── */
  function watchUrlChanges() {
    var lastUrl = window.location.href;

    window.addEventListener('popstate', function() {
      if (window.location.href !== lastUrl) {
        lastUrl = window.location.href;
        setTimeout(scanAndGate, 200);
        setTimeout(scanAndGate, 500);
      }
    });

    var origPush = history.pushState;
    var origReplace = history.replaceState;

    history.pushState = function() {
      origPush.apply(this, arguments);
      if (window.location.href !== lastUrl) {
        lastUrl = window.location.href;
        setTimeout(scanAndGate, 200);
        setTimeout(scanAndGate, 500);
      }
    };

    history.replaceState = function() {
      origReplace.apply(this, arguments);
      if (window.location.href !== lastUrl) {
        lastUrl = window.location.href;
        setTimeout(scanAndGate, 200);
        setTimeout(scanAndGate, 500);
      }
    };
  }

  function watchDomChanges() {
    var gallery = document.querySelector(
      '.product__media-list, .product-gallery, .product__media-wrapper, [data-product-media-list], .product, .main-product, main'
    );

    if (!gallery) return;

    var observer = new MutationObserver(function(mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if ((m.type === 'attributes' &&
            (m.attributeName === 'class' || m.attributeName === 'style' || m.attributeName === 'aria-hidden')) ||
            (m.type === 'childList' && (m.addedNodes.length > 0 || m.removedNodes.length > 0))) {
          debouncedScan();
          return;
        }
      }
    });

    observer.observe(gallery, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'aria-hidden', 'hidden'],
      childList: true
    });
  }

  function watchVariantEvents() {
    ['variant:changed', 'theme:variant:changed', 'foxkit:variant:change', 'product:variant-change'].forEach(function(evt) {
      document.addEventListener(evt, function() {
        setTimeout(scanAndGate, 200);
        setTimeout(scanAndGate, 500);
      });
    });

    document.addEventListener('change', function(e) {
      if (e.target.closest('variant-selects, variant-radios, .product-form__variants, [data-variant-selects], form[action*="/cart/add"]')) {
        setTimeout(scanAndGate, 200);
        setTimeout(scanAndGate, 500);
      }
    });
  }

  /* ── INIT ─────────────────────────────────────────────────────── */
  function init() {
    if (isRunning) return;
    isRunning = true;

    /* Disconnect the early interceptor observer (its job is done) */
    if (window.__mediaLoadGateObserver) {
      window.__mediaLoadGateObserver.disconnect();
    }

    /* Give Star App time to do its initial show/hide pass */
    setTimeout(function() {
      scanAndGate();
      watchUrlChanges();
      watchDomChanges();
      watchVariantEvents();

      /* Safety rescans */
      setTimeout(scanAndGate, 600);
      setTimeout(scanAndGate, 1200);

      console.log('[MediaLoadGate] Active. Only visible variant images will download.');
    }, 300);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();